import React, { useState,useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  Box,
  Checkbox,
  Button,
  Toolbar,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapVert as SwapVertIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
  getComparator,
  stableSort,
} from "../../../common-utilities/sortingUtils";
import AssignSlatesDialog from "../common/AssignSlatesDialog";
import AssignTestsDialog from "../common/AssignTestsDialog";
import DeleteConfirmation from "../common/DeleteConfirmation";
import AddEditQuoteTransactionDialog from "./AddEditQuoteTransactionDialog"
import ReorderQuoteTransactionsDialog from "./ReorderQuoteTransactionsDialog"
import { DeleteQuoteTransaction } from "../../../vlp/services/QuotesService";

const QuotransTable = ({ quotransData, quoteNumber, accountNumber, onRefresh }) => {
  const navigate = useNavigate();
  const columns = [
    "Description",
    "Detail",
    "Frequency",
    "Test Code",
    "Quantity",
    "Unit Price",
    "List Price",
    "Actions",
  ];

  const columnHeaders = [
    "description",
    "detail",
    "frequency",
    "testCode",
    "quantity",
    "unitPrice",
    "listPrice",
  ];

  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [assignSlatesDialogOpen, setAssignSlatesDialogOpen] = useState(false);
  const [assignTestsDialogOpen, setAssignTestsDialogOpen] = useState(false);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);

  // Delete confirmation
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedRecordToDelete, setSelectedRecordToDelete] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [localQuotransData, setLocalQuotransData] = useState(quotransData || []);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

useEffect(() => {
  setLocalQuotransData(quotransData || []);
}, [quotransData]);
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleCheckboxChange = (recordId) => {
    setSelectedRecords((prevSelected) =>
      prevSelected.includes(recordId)
        ? prevSelected.filter((id) => id !== recordId)
        : [...prevSelected, recordId]
    );
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRecords(localQuotransData.map((record) => record.recordid));
    } else {
      setSelectedRecords([]);
    }
  };

  // Assign Slates Dialog handlers
  const handleOpenAssignSlatesDialog = () => setAssignSlatesDialogOpen(true);
  const handleCloseAssignSlatesDialog = () => {
    setAssignSlatesDialogOpen(false);
    if (onRefresh) onRefresh();
  };

  const handleAssignSlatesComplete = () => {
    window.location.reload();
    if (onRefresh) onRefresh();
  };

  // Assign Tests Dialog handlers
  const handleOpenAssignTestsDialog = () => setAssignTestsDialogOpen(true);
  const handleCloseAssignTestsDialog = () => {
    setAssignTestsDialogOpen(false);
    if (onRefresh) onRefresh();
  };

  const handleAssignTestsComplete = () => {
    window.location.reload();
    if (onRefresh) onRefresh();
  };

  // Re-order Items dialog handlers
  const handleOpenReorderDialog = () => setReorderDialogOpen(true);
  const handleCloseReorderDialog = () => setReorderDialogOpen(false);

  const handleReorderComplete = (orderedRecords) => {
    // Mirror the sort_order the API just wrote (steps of 10) so the grid matches the
    // saved order without needing a reload - AddQuote does not pass onRefresh.
    setLocalQuotransData(
      orderedRecords.map((record, index) => ({
        ...record,
        sortOrder: (index + 1) * 10,
      }))
    );
    // Drop any column sort so the grid falls back to the stored order the user just set.
    setOrderBy("");
    setOrder("asc");
    setPage(0);
    if (onRefresh) onRefresh();
  };

  // --- Actions ---
  const handleAddNewClick = () => {
    setEditingRecordId(null);
    setDialogOpen(true);
  };

  const handleEditClick = (record) => {
    setEditingRecordId(record.recordid);
    setDialogOpen(true);
  };

  const handleViewClick = (record) => {
    const viewUrl = `/vlp/quotes/${record.quoteNumber}`;
    if (window.quoteViewWindow && !window.quoteViewWindow.closed) {
      window.quoteViewWindow.location.href = viewUrl;
    } else {
      window.quoteViewWindow = window.open(viewUrl, "_blank");
    }
  };

  const handleDeleteClick = (record) => {
    setSelectedRecordToDelete(record);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    if (!selectedRecordToDelete) return;

    try {
      const response = await DeleteQuoteTransaction(
        selectedRecordToDelete.quoteNumber,
        selectedRecordToDelete.recordid
      );

      if (response && (response.statusCode === 200 || response.success)) {
        NotificationManager.success("Quote transaction deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setLocalQuotransData(prevData =>
          prevData.filter(item => item.recordid !== selectedRecordToDelete.recordid)
        );
        if (onRefresh) onRefresh();
      } else {
        throw new Error("Failed to delete quote transaction");
      }
    } catch (error) {
      NotificationManager.error("Error deleting transaction");
    }
  };

  const renderActions = (record) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton aria-label="edit" style={{ color: "orange" }} onClick={() => handleEditClick(record)}>
        <EditIcon />
      </IconButton>
      {/* <IconButton aria-label="view" style={{ color: "green" }} onClick={() => handleViewClick(record)}>
        <VisibilityIcon />
      </IconButton> */}
      <IconButton aria-label="delete" style={{ color: "red" }} onClick={() => handleDeleteClick(record)}>
        <DeleteIcon />
      </IconButton>
    </div>
  );

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `$${value.toFixed(2)}`;
  };

  const formatValue = (column, value) => {
    if (value === null || value === undefined) return "-";
    switch (column) {
      case "unitPrice":
      case "listPrice":
        return formatCurrency(value);
      case "quantity":
        return value.toFixed(2);
      default:
        return value;
    }
  };

  const sortedData = stableSort(localQuotransData || [], getComparator(order, orderBy));
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Paper>
        {/* Toolbar */}
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            gap: 1,
            flexWrap: "wrap",
            bgcolor: (theme) =>
              selectedRecords.length > 0
                ? theme.palette.action.selected
                : "transparent",
          }}
        >
          {selectedRecords.length > 0 ? (
            <Typography sx={{ flex: "1 1 auto" }} color="inherit" variant="subtitle1">
              {selectedRecords.length} selected
            </Typography>
          ) : (
            <Typography sx={{ flex: "1 1 auto" }} variant="h6">
              Quote Transactions
            </Typography>
          )}

          {/* Action buttons. Kept in their own flex row so they wrap as a group and
              never get squeezed by the title. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Re-order Items */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SwapVertIcon />}
              onClick={handleOpenReorderDialog}
              disabled={(localQuotransData?.length || 0) < 2}
              title="Click here to re-order items assigned to this quote"
              sx={{ fontSize: "9pt", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Re-order Items
            </Button>

            {/* Add New Transaction */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNewClick}
              sx={{ fontSize: "9pt", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Transaction
            </Button>

            {/* Assign Tests - NEW */}
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleOpenAssignTestsDialog}
              sx={{ fontSize: "9pt", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Tests
            </Button>

            {/* Assign Slates */}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={handleOpenAssignSlatesDialog}
              sx={{ fontSize: "9pt", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Slates
            </Button>
          </Box>
        </Toolbar>

        {/* Table */}
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <TableContainer sx={{ maxHeight: 900, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedRecords.length > 0 &&
                        selectedRecords.length < localQuotransData?.length
                      }
                      checked={
                        localQuotransData?.length > 0 &&
                        selectedRecords.length === localQuotransData.length
                      }
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  {columns.map((column, index) => (
                    <TableCell
                      key={column}
                      sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      {column !== "Actions" ? (
                        <TableSortLabel
                          active={orderBy === columnHeaders[index]}
                          direction={
                            orderBy === columnHeaders[index] ? order : "asc"
                          }
                          onClick={() =>
                            handleRequestSort(columnHeaders[index])
                          }
                        >
                          {column}
                        </TableSortLabel>
                      ) : (
                        column
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      <Typography variant="body2" sx={{ py: 4 }}>
                        No transactions found. Click "+ Transaction" to add a new one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((record, index) => (
                    <TableRow
                      key={record.recordid}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRecords.includes(record.recordid)}
                          onChange={() => handleCheckboxChange(record.recordid)}
                        />
                      </TableCell>

                      {columnHeaders.map((column) => {
                        const value = formatValue(column, record[column]);
                        const isLongTextColumn = column === "description" || column === "detail";

                        const displayValue =
                          isLongTextColumn && value !== "-" && value.length > 15
                            ? value.substring(0, 15) + "..."
                            : value;

                        return (
                          <TableCell
                            key={column}
                            sx={{ whiteSpace: "nowrap" }}
                            title={isLongTextColumn ? value : ""}
                          >
                            {displayValue}
                          </TableCell>
                        );
                      })}

                      <TableCell>{renderActions(record)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={localQuotransData?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Assign Slates Dialog */}
      <AssignSlatesDialog
        open={assignSlatesDialogOpen}
        onClose={handleCloseAssignSlatesDialog}
        mode="QUOTE"
        quoteNumber={quoteNumber}
        accountNumber={accountNumber}
        onAssignComplete={handleAssignSlatesComplete}
      />

      {/* Assign Tests Dialog - NEW */}
      <AssignTestsDialog
        open={assignTestsDialogOpen}
        onClose={handleCloseAssignTestsDialog}
        itemType="Quote"
        itemLabel="Quote:"
        itemValue={quoteNumber}
        matrix="All"
        onAssignComplete={handleAssignTestsComplete}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this transaction?"
        color="red"
      />

      {/* Re-order Items Dialog */}
      <ReorderQuoteTransactionsDialog
        open={reorderDialogOpen}
        onClose={handleCloseReorderDialog}
        quoteNumber={quoteNumber}
        quotransData={localQuotransData}
        onReorderComplete={handleReorderComplete}
      />

      <AddEditQuoteTransactionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        quoteNumber={quoteNumber}
        recordId={editingRecordId}
        onSaveComplete={() => window.location.reload()}
      />
    </>
  );
};

export default QuotransTable;